import { sortBy } from "lodash-es";

import type { BoardType } from "@/model/baseTypes";
import { useArtStore } from "@/store/art";
import { useBoardsStore } from "@/store/boards";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";

export type Grouped<T> = Record<string, T[]>;
type Groups<T> = Array<[string, T[]]>;

export function sortedBoardIds<T>(grouped: Grouped<T>): Groups<T> {
  return sortBy(Object.entries(grouped), ([boardId]) => boardIndex(boardId));
}

export function sortedTypeNames<T>(grouped: Grouped<T>): Groups<T> {
  return sortBy(Object.entries(grouped), ([typeName]) =>
    useStickyTypeStore().stickyTypes.findIndex((type) => type.name === typeName),
  );
}

export function sortedTeamNames<T>(grouped: Grouped<T>): Groups<T> {
  return sortBy(Object.entries(grouped), ([teamName]) =>
    useTeamStore().teamsInCurrentArt.findIndex((team) => team.name === teamName),
  );
}

export function sortedTeamFilters<T>(grouped: Grouped<T>): Groups<T> {
  return sortBy(Object.entries(grouped), ([teamFilter]) => teamFilterIndex(teamFilter));
}

export function sortedArtIds<T>(grouped: Grouped<T>): Groups<T> {
  return sortBy(Object.entries(grouped), ([artId]) =>
    useArtStore().arts.findIndex((art) => art.id === artId),
  );
}

const boardTypeOrder: BoardType[] = [
  "solution_backlog",
  "solution",
  "backlog",
  "program",
  "objectives",
  "risk",
  "team",
  "flex",
];

function boardIndex(boardId: string) {
  const boardType = useBoardsStore().boardById(boardId).type;
  return boardTypeOrder.findIndex((type) => boardType === type);
}

function teamFilterIndex(teamFilter: string) {
  switch (teamFilter) {
    case "depTeamFilter.blocked":
      return 1;
    case "depTeamFilter.enabling":
      return 2;
    default:
      return 3;
  }
}
